import React, { useState, useEffect, useRef } from 'react';
import { Plus, Settings } from 'lucide-react';
import { apiService, CustomFieldDefinition } from '../services/api';
import { User } from '../types';
import CustomFieldModal from './CustomFieldModal';
import './ColumnManager.css';

interface ColumnManagerProps {
  visibleColumns: string[];
  onToggleColumn: (columnId: string) => void;
  onRefresh?: () => void;
  user?: User | null;
  refreshTrigger?: number; // Incrementing number to trigger custom fields refresh
}

interface ColumnOption {
  id: string;
  label: string;
  type: 'builtin' | 'custom';
  visible: boolean;
}

const ColumnManager: React.FC<ColumnManagerProps> = ({ 
  visibleColumns, 
  onToggleColumn,
  onRefresh,
  user,
  refreshTrigger
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Mandatory columns that cannot be toggled (excluded from this UI)
  const MANDATORY_COLUMNS = ['name'];
  
  // Built-in columns that can be toggled (excluding mandatory columns)
  // Only include actual Founder model fields, not custom fields
  const builtinColumns: ColumnOption[] = [
    { id: 'no', label: 'No', type: 'builtin', visible: visibleColumns.includes('no') },
    { id: 'profileUrl', label: 'Profile URL', type: 'builtin', visible: visibleColumns.includes('profileUrl') },
    { id: 'lastPostExcerpt', label: 'Last Post', type: 'builtin', visible: visibleColumns.includes('lastPostExcerpt') },
    { id: 'lastPostDate', label: 'Last Post Date', type: 'builtin', visible: visibleColumns.includes('lastPostDate') },
    { id: 'nextAction', label: 'Next Action', type: 'builtin', visible: visibleColumns.includes('nextAction') },
    { id: 'update', label: 'Update', type: 'builtin', visible: visibleColumns.includes('update') }
  ];

  // Custom columns from custom fields (hide Tag/Tags since Tag is built-in)
  const customColumns: ColumnOption[] = customFields
    .filter(field => {
      const name = (field as any).name ? String((field as any).name).toLowerCase() : '';
      const label = field.label ? field.label.toLowerCase() : '';
      return !(name === 'tag' || name === 'tags' || label === 'tag' || label === 'tags');
    })
    .map(field => ({
      id: field.id,
      label: field.label,
      type: 'custom' as const,
      visible: visibleColumns.includes(field.id)
    }));


  useEffect(() => {
    fetchCustomFields();
  }, []);

  // Refresh custom fields when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchCustomFields();
    }
  }, [refreshTrigger]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // Check if click is not on the trigger button
        const triggerButton = document.getElementById('column-manager-trigger');
        if (triggerButton && triggerButton.contains(event.target as Node)) {
          return; // Don't close if clicking the trigger button (it will toggle)
        }
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fetchCustomFields = async () => {
    try {
      setLoading(true);
      const fields = await apiService.getCustomFields();
      setCustomFields(fields);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleColumn = (columnId: string) => {
    // Prevent toggling mandatory columns
    const MANDATORY_COLUMNS = ['name'];
    if (MANDATORY_COLUMNS.includes(columnId)) {
      return; // Don't allow toggling mandatory columns
    }
    onToggleColumn(columnId);
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };


  const handleFieldCreated = (newField: CustomFieldDefinition) => {
    setCustomFields(prev => [...prev, newField]);
    // Automatically show the new column
    onToggleColumn(newField.id);
  };

  const handleDeleteCustomField = async (fieldId: string) => {
    if (window.confirm('Are you sure you want to delete this custom column? This will remove all data for this field.')) {
      try {
        await apiService.deleteCustomField(fieldId);
        setCustomFields(prev => prev.filter(field => field.id !== fieldId));
        // Remove from visible columns if it was visible
        if (visibleColumns.includes(fieldId)) {
          onToggleColumn(fieldId);
        }
      } catch (error) {
        console.error('Error deleting custom field:', error);
        alert('Failed to delete custom field');
      }
    }
  };


  return (
    <div className="column-manager-floating" ref={dropdownRef}>
      <button 
        id="column-manager-trigger"
        className="floating-columns-button"
        onClick={handleToggleDropdown}
        title="Manage columns"
      >
        <Settings size={18} />
      </button>

        {isOpen && (
          <div className="column-dropdown-menu">
            <div className="column-sections">
              {/* Built-in columns */}
              <div className="column-section">
                <div className="section-header">
                  <span>Built-in Columns</span>
                </div>
                {builtinColumns.map(column => (
                  <div key={column.id} className="column-option">
                    <label className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={column.visible}
                        onChange={() => handleToggleColumn(column.id)}
                      />
                      <span className="checkmark"></span>
                      <span className="column-label">{column.label}</span>
                    </label>
                  </div>
                ))}
              </div>

              {/* Custom columns */}
              <div className="column-section">
                <div className="section-header">
                  <span>Custom Columns</span>
                  <button
                    className="add-custom-button"
                    onClick={() => setShowModal(true)}
                    title="Add new custom column"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                
                {loading ? (
                  <div className="loading-text">Loading custom columns...</div>
                ) : customColumns.length === 0 ? (
                  <div className="empty-state">
                    <span>No custom columns yet</span>
                    <button 
                      className="create-first-button"
                      onClick={() => setShowModal(true)}
                    >
                      Create your first column
                    </button>
                  </div>
                ) : (
                  customColumns.map(column => (
                    <div key={column.id} className="column-option custom-column">
                      <label className="column-checkbox">
                        <input
                          type="checkbox"
                          checked={column.visible}
                          onChange={() => handleToggleColumn(column.id)}
                        />
                        <span className="checkmark"></span>
                        <span className="column-label">{column.label}</span>
                      </label>
                      <button
                        className="delete-custom-button"
                        onClick={() => handleDeleteCustomField(column.id)}
                        title="Delete custom column"
                      >
                        <Settings size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

      <CustomFieldModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onFieldCreated={handleFieldCreated}
      />
    </div>
  );
};

export default ColumnManager;
